<script setup lang="ts">
import { type Slots, toRefs, useSlots } from 'vue'

import cn from '@satch-ui/lib/classMerge'
import { useBlockScroll } from 'internal/helper'

import { CloseCircleIcon } from '@fond-of/satch-icons'
import { vE2eTestId } from '@fond-of/telemetry'

const props = withDefaults(
  defineProps<{
    to?: string
    isOpen: boolean
    showCloseIcon?: boolean
    hasContainerPadding?: boolean
    class?: string
  }>(),
  {
    to: 'body',
    showCloseIcon: true,
    hasContainerPadding: true,
    class: undefined,
  }
)

const { isOpen, to, showCloseIcon } = toRefs(props)

const slots: Slots = useSlots()

const emits = defineEmits<{
  (e: 'close'): () => void
}>()

useBlockScroll(isOpen)
</script>

<template>
  <Teleport
    :to="to"
    :disabled="!isOpen"
  >
    <Transition
      name="fade-in"
      appear
    >
      <div
        v-if="isOpen"
        v-e2e-test-id="'modalCheckout'"
        :class="
          cn(
            'bg-gray-medium z-modal-checkout lg:z-modal-checkout-desktop fixed bottom-0 left-0 right-0 top-[50px] h-full w-full cursor-pointer lg:top-0 lg:bg-opacity-50',
            props.class
          )
        "
        @click="emits('close')"
      >
        <div
          class="flex h-[calc(100dvh-50px)] w-screen items-center justify-center lg:h-full lg:w-full"
        >
          <div
            class="relative h-[calc(100dvh-50px)] w-screen cursor-default overflow-y-auto bg-white lg:h-auto lg:w-auto lg:max-w-[960px] lg:pb-[3.8125rem] lg:pl-20 lg:pr-[6.25rem] lg:pt-[4.6875rem] lg:shadow-2xl"
            :class="{ 'p-5 lg:p-20': props.hasContainerPadding }"
            @click.stop
          >
            <slot name="header" />
            <div
              class="flex h-full w-full flex-col text-xs text-black lg:flex-row"
            >
              <div
                v-if="!!slots.contentLeft && !!!slots.contentCenter"
                v-e2e-test-id="'modalCheckoutContentLeft'"
                class="w-full lg:w-6/12"
              >
                <slot name="contentLeft" />
              </div>
              <div
                v-if="
                  !!slots.contentLeft &&
                  !!slots.contentRight &&
                  !!!slots.contentCenter
                "
                v-e2e-test-id="'modalCheckoutDivider'"
                class="bg-gray-light my-6 h-0.5 w-full lg:my-0 lg:ml-[7.5rem] lg:mr-[6.125rem] lg:h-[22.375rem] lg:w-0.5"
              />
              <div
                v-if="
                  !!slots.contentRight &&
                  !!slots.contentLeft &&
                  !!!slots.contentCenter
                "
                v-e2e-test-id="'modalCheckoutContentRight'"
                class="w-full lg:w-6/12"
              >
                <slot name="contentRight" />
              </div>
              <div
                v-if="!!slots.contentCenter"
                v-e2e-test-id="'modalCheckoutContentCenter'"
                class="size-full"
              >
                <slot name="contentCenter" />
              </div>
            </div>

            <div
              v-if="!!slots.footer"
              class="text-center font-sans text-xs"
            >
              <slot name="footer" />
            </div>
            <CloseCircleIcon
              v-if="showCloseIcon"
              class="h-7.5 w-7.5 absolute right-3 top-3 z-10 cursor-pointer text-black lg:block"
              @click="emits('close')"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.fade-in-enter-active {
  transition: all 500ms ease-out;
}

.fade-in-leave-active {
  transition: none;
}

.fade-in-enter-from,
.fade-in-leave-to {
  opacity: 0;
}
</style>
